import React, { useState, useRef, useEffect } from 'react';
import { imgUrl, imgSrcSet, imgBlurPlaceholder, GRID_WIDTHS } from '../utils/imageUrl';
import './SmartImage.css';

/**
 * Product photography wrapper.
 *
 * Solves three things that were causing layout problems:
 *  · The box reserves its space *before* the photo arrives, using
 *    `aspect-ratio`, so nothing on the page jumps as images load.
 *  · Every photo is cropped to the same ratio with `object-fit`,
 *    so a portrait phone snap and a square studio shot sit side by
 *    side without one of them looking stretched.
 *  · A blurred 24px version shows immediately, then cross-fades to
 *    the real photo — no blank grey rectangles.
 *
 * @param {string}  src        image URL
 * @param {string}  alt
 * @param {string}  [ratio]    any CSS aspect-ratio, e.g. '1 / 1'
 * @param {string}  [sizes]    responsive sizes attribute
 * @param {number[]}[widths]   srcset widths
 * @param {boolean} [priority] skip lazy-loading (above-the-fold only)
 * @param {React.ReactNode} [fallback] shown when there is no src
 */
export default function SmartImage({
  src,
  alt = '',
  ratio,
  sizes = '(max-width: 767px) 33vw, (max-width: 1024px) 25vw, 300px',
  widths = GRID_WIDTHS,
  priority = false,
  fallback = null,
  className = '',
  imgClassName = '',
  onClick,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // Reset on every src change: naya src aate hi blur placeholder dikhao,
  // purani photo stale na rahe. A cached image can finish loading before
  // React attaches onLoad, so mark it loaded immediately in that case.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
    else setLoaded(false);
  }, [src]);

  const style = ratio ? { aspectRatio: ratio } : undefined;

  if (!src) {
    return (
      <div className={`smart-img smart-img--empty ${className}`} style={style} onClick={onClick} {...rest}>
        {fallback}
      </div>
    );
  }

  const blur = imgBlurPlaceholder(src);
  const widest = widths[widths.length - 1];

  return (
    <div
      className={`smart-img ${loaded ? 'is-loaded' : 'is-loading'} ${className}`}
      style={style}
      onClick={onClick}
      {...rest}
    >
      {blur && (
        <img
          className="smart-img__blur"
          src={blur}
          alt=""
          aria-hidden="true"
          draggable="false"
        />
      )}
      <img
        ref={imgRef}
        className={`smart-img__real ${imgClassName}`}
        src={imgUrl(src, { w: widest })}
        srcSet={imgSrcSet(src, widths)}
        sizes={sizes}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchpriority={priority ? 'high' : undefined}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        draggable="false"
      />
    </div>
  );
}